import "../../css/GlobalStyles.css";
import { CSSProperties, ReactNode, memo, useMemo } from "react";
import { Colors } from "../../utils/colors";
import { CreatorPanelTabs } from "../Tabs/CreatorPanelTabs";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { TeamMemberRole } from "@markit/common.types";
import EmptyStateButton from "../Buttons/EmptyStateButton";
import { Icon } from "@iconify/react";
import { onCreatorPanelNavigatePath } from "../../utils/navigationUtils";
import { useNavigate } from "react-router-dom";
import { NavigationId } from "../../navigation/AppParamList";
import { Helmet } from "react-helmet";

export type CreatorModeHeaderTab = {
  tabNames: string[];
  tabRightIcons?: ReactNode[];
  tabValue: number;
  setTabValue: (tabValue: number) => void;
};

type CreatorModePanelWrapperProps = {
  title: string | ReactNode;
  subtext?: string;
  children: ReactNode;
  headerRight: ReactNode;
  headerTabs?: CreatorModeHeaderTab;
  headerContainerStyles?: CSSProperties;
  preventScroll?: boolean;
  allowAccess?: boolean; // to conditionally show content if user has restricted access
};

const CreatorModePanelWrapper = (props: CreatorModePanelWrapperProps) => {
  const {
    title,
    subtext,
    children,
    headerRight,
    headerTabs,
    headerContainerStyles,
    preventScroll,
    allowAccess,
  } = props;
  const { teamMemberData, accountData } = useSelector(getAccountState).account;
  const navigate = useNavigate();

  const requestAccess = useMemo(
    () =>
      teamMemberData &&
      teamMemberData.role === TeamMemberRole.TWO_WAY_SUPPORT &&
      !allowAccess,
    [teamMemberData, allowAccess]
  );

  const renderRequestAccess = useMemo(
    () => (
      <>
        <EmptyStateButton
          title={"Request Access"}
          description={
            "This feature is only available for team admins. Contact your team owner to request access."
          }
          icon={
            <Icon
              icon={"ion:lock-closed-sharp"}
              height={52}
              style={{ color: Colors.GRAY1 }}
            />
          }
          iconBox={80}
          btnText={"View Team"}
          onPress={() =>
            onCreatorPanelNavigatePath(
              navigate,
              NavigationId.HOME_SETTINGS,
              teamMemberData?.role === TeamMemberRole.TWO_WAY_SUPPORT ? 1 : 3
            )
          }
          containerStyles={{ margin: "auto" }}
        />
      </>
    ),
    [navigate, teamMemberData?.role]
  );

  return (
    <div
      className={`ColumnNormal ${preventScroll ? "PreventScroll" : ""}`}
      style={{ height: "100%" }}
    >
      <Helmet>
        <title>
          {accountData.fullName
            ? `${accountData.fullName} - Markit AI`
            : "Markit AI"}
        </title>
        <meta name="og:title" content={"Markit AI"} />
        <meta name="og:description" content={""} />
      </Helmet>
      <div
        className="AlignedRowSpaced"
        style={{
          alignItems: "flex-start",
          padding: 30,
          borderBottom: `1px solid ${Colors.GRAY11}`,
          ...headerContainerStyles,
        }}
      >
        <div className="ColumnNormal" style={{ gap: 20 }}>
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <span style={{ fontSize: 32, fontWeight: 600 }}>{title}</span>
            {subtext ? <span className="bodySubtext">{subtext}</span> : null}
          </div>
          {headerTabs && !requestAccess ? (
            <CreatorPanelTabs
              tabValue={headerTabs.tabValue}
              setTabValue={headerTabs.setTabValue}
              tabNames={headerTabs.tabNames}
              tabRightIcons={headerTabs.tabRightIcons}
            />
          ) : null}
        </div>
        {requestAccess ? undefined : headerRight}
      </div>
      <div
        className={`ColumnNormal ${preventScroll ? "PreventScroll" : ""}`}
        style={{ backgroundColor: Colors.WHITE1, flex: 1 }}
      >
        {requestAccess ? renderRequestAccess : children}
      </div>
    </div>
  );
};

export default memo(CreatorModePanelWrapper);
